import { apiUrl } from "../api/endpoints";
import React, { useEffect, useState, useContext, useCallback } from 'react';
import '../Components/modal.css';
import './edit.css';
import './DownloadReportShared.css';
import axios from 'axios';

import Select from 'react-select';
import Alert from '../Components/Alert';

import dashTranslations from '../languages/dashboard.json';
import webDashboardTranslations from '../languages/webDashboard.json';
import { LanguageContext } from '../context/LanguageContext';
import { getErrorMessage } from '../utils/errorUtils';
import { useAuth } from '../hooks/AuthProvider';
import { useFontSize } from '../context/FontSizeContext';
import WebDashboardLayoutModeField from '../Components/WebDashboardLayoutModeField';
import AiReportTypeField from '../Components/AiReportTypeField';
import WorkDaysHoursPicker from '../Components/WorkDaysHoursPicker';
import { useAiReportTypes } from '../utils/aiReportTypes';
import {
    createDefaultWebDashboardTab,
    WEB_DASHBOARD_LAYOUT_MODES,
} from '../utils/webDashboardLayout';
import { normalizeOrganizationListRow } from '../utils/listRowIds';

import { NameIcon, AddNewIcon } from '../svg/UtilsIcons';
import { OrgNameIcon } from '../svg/OrganizationIcons';
import { TemplateIcon } from '../svg/TemplateIcons'; 

export const AddDashboard = ({
    bg,
    fg,
    ft,
    textColor,
    btnPrimary,
    btnSecondary,
    setOpen,
    refresh
}) => {
    const { lang } = useContext(LanguageContext);
    const { permissions } = useAuth();
    useFontSize();

    const canAddNewDashboard = permissions['add_new_dashoard'];

    const t = dashTranslations[lang].addDashboard;
    const webDashboardT = webDashboardTranslations[lang]?.webDashboard || {};
    const isRtl = ['ar', 'ur'].includes(lang);

    const [creationMode, setCreationMode] = useState('blank');
    const { options: aiReportTypeOptions, availability: aiReportAvailability } = useAiReportTypes();
    const showAiReportTypeField = aiReportAvailability.dashboards !== false;
    const [examples, setExamples] = useState([]);
    const [loadingExamples, setLoadingExamples] = useState(false);
    const [submitting, setSubmitting] = useState(false);

    const [formData, setFormData] = useState({
        name: '',
        description: '',
        type: null,
        organization_id: null,
        dashboard_example_id: null,
        initialTabLayoutMode: WEB_DASHBOARD_LAYOUT_MODES.PIXELS,
        work_days_hours: [],
        dashboard_report_devices: [],
    });

    const [organizations, setOrganizations] = useState([]);
    const [reportDeviceOptions, setReportDeviceOptions] = useState([]);
    const [loadingReportDevices, setLoadingReportDevices] = useState(false);

    const [alertState, setAlertState] = useState({
        open: false,
        message: '',
        type: 'info',
        position: isRtl ? 'left' : 'right'
    });

    const showAlert = useCallback((type, message) => {
        setAlertState({
            open: true,
            type,
            message,
            position: isRtl ? 'left' : 'right'
        });
    }, [isRtl]);

    // Load organizations
    useEffect(() => {
        if (!canAddNewDashboard) return;

        const fetchOrganizations = async () => {
            try {
                const res = await axios.get(apiUrl(`organization/list`));

                if (res.data && Array.isArray(res.data.data)) {
                    setOrganizations(res.data.data.map(normalizeOrganizationListRow));
                } else {
                    console.error(
                        'Received unexpected data format from /organization/list:',
                        res.data
                    );
                    setOrganizations([]);
                    showAlert('error', 'Received an invalid format from the server.');
                }
            } catch (err) {
                const errorMessage = getErrorMessage(err);
                showAlert('error', errorMessage);
                setOrganizations([]);
            }
        };

        fetchOrganizations();
    }, [canAddNewDashboard, showAlert]);

    useEffect(() => {
        let active = true;
        if (!canAddNewDashboard || !formData.organization_id) {
            setReportDeviceOptions([]);
            return undefined;
        }

        setLoadingReportDevices(true);
        axios.get(apiUrl(`dashboard/report-devices/options/${formData.organization_id}`))
            .then((res) => {
                if (!active) return;
                const rows = Array.isArray(res?.data?.data) ? res.data.data : [];
                setReportDeviceOptions(rows.map((device) => ({
                    value: Number(device.id),
                    label: `${device.name || device.did} — ${device.did}${device.organization_name ? ` — ${device.organization_name}` : ''}`,
                    device,
                })));
            })
            .catch((err) => {
                if (!active) return;
                console.error('Failed to load dashboard report devices', err);
                setReportDeviceOptions([]);
            })
            .finally(() => { if (active) setLoadingReportDevices(false); });

        return () => { active = false; };
    }, [canAddNewDashboard, formData.organization_id]);

    useEffect(() => {
        const fetchExamples = async () => {
            if (!canAddNewDashboard) return;
            if (creationMode !== 'example' || examples.length > 0) return;

            setLoadingExamples(true);
            try {
                const res = await axios.get(apiUrl(`dashboard/examples/list`));
                if (res.data && res.data.status === 'OK') {
                    setExamples(
                        res.data.data.map((example) => ({
                            value: example.dashboard_example_id ?? example.id,
                            label: example.example_name,
                            ...example,
                        }))
                    );
                } else {
                    throw new Error('Invalid examples response');
                }
            } catch (err) {
                console.error('Failed to load dashboard examples', err);
                showAlert('error', t.loadExamplesError || 'Failed to load dashboard examples.');
            } finally {
                setLoadingExamples(false);
            }
        };

        fetchExamples();
    }, [creationMode, canAddNewDashboard, examples.length, showAlert, t.loadExamplesError]);

    const handleModeSwitch = (mode) => {
        setCreationMode(mode);
        setFormData({
            name: '',
            description: '',
            type: null,
            organization_id: null,
            dashboard_example_id: null,
            initialTabLayoutMode: WEB_DASHBOARD_LAYOUT_MODES.PIXELS,
            work_days_hours: [],
            dashboard_report_devices: [],
        });
    };

    const onChange = e =>
        setFormData(prev => ({
            ...prev,
            [e.target.name]: e.target.value
        }));

    const handleOrgSelect = selectedOption => {
        setFormData(prev => ({
            ...prev,
            organization_id: selectedOption ? selectedOption.value : null,
            dashboard_report_devices: []
        }));
    };

    const handleExampleChange = (selectedOption) => {
        if (selectedOption) {
            setFormData((prev) => ({
                ...prev,
                dashboard_example_id: selectedOption.value,
                description: selectedOption.description || '',
            }));
            return;
        }

        setFormData((prev) => ({
            ...prev,
            dashboard_example_id: null,
            description: '',
        }));
    };

    const createDashboard = async () => {
        if (submitting) return;

        if (!formData.name.trim()) {
            showAlert(
                'error',
                t.validation?.nameRequired || 'Dashboard name is required.'
            );
            return;
        }

        if (!formData.organization_id) {
            showAlert(
                'error',
                t.validation?.orgRequired || 'Organization is required.'
            );
            return;
        }

        if (creationMode === 'example' && !formData.dashboard_example_id) {
            showAlert(
                'error',
                t.validation?.exampleRequired || 'Please select an example.'
            );
            return;
        }

        try {
            const payload = {
                name: formData.name,
                description: formData.description,
                type: formData.type,
                organization_id: formData.organization_id,
                work_days_hours: formData.work_days_hours,
                dashboard_report_devices: formData.dashboard_report_devices,
            };

            if (creationMode === 'example') {
                payload.create_example = true;
                payload.dashboard_example_id = formData.dashboard_example_id;
            } else {
                payload.webdashboard = [
                    createDefaultWebDashboardTab({
                        name: 'Dashboard',
                        layoutMode: formData.initialTabLayoutMode,
                    }),
                ];
            }

            setSubmitting(true);
            await axios.post(apiUrl(`dashboard/create`), payload, {
                headers: { 'Content-Type': 'application/json' }
            });

            showAlert(
                'success',
                t.successMessage || 'Dashboard created successfully.'
            );
            refresh();

            setTimeout(() => {
                setOpen(false);
            }, 2000);
        } catch (err) {
            const errorMessage = getErrorMessage(err);
            showAlert('error', errorMessage);
            setSubmitting(false);
        }
    };

    // PERMISSION DENIED MODAL (same visual style as AddDevice)
    if (!canAddNewDashboard) {
        return (
            <div
                id="modal"
                style={{
                    direction: isRtl ? 'rtl' : 'ltr'
                }}
            >
                <div
                    id="popup"
                    className="dashboard-modal"
                    style={{
                        animationName: isRtl ? 'reverse-add-zoom' : 'add-zoom',
                        fontSize:
                            'calc(var(--body-font-size) * var(--font-size-multiplier))'
                    }}
                >
                    <div className="dashboard-header">
                        <div className="dashboard-header-icon">
                            <AddNewIcon />
                        </div>
                        <div className="dashboard-header-text">
                            <h2>
                                {t.permissionDenied?.title || 'Permission Denied'}
                            </h2>
                            <p>
                                {t.permissionDenied?.message ||
                                    'You do not have permission to add a new dashboard.'}
                            </p>
                        </div>
                    </div>

                    <div className="modal-divider" />

                    <div className="modal-content dashboard-content" />

                    <div className="modal-footer dashboard-footer">
                        <button
                            className="btn btn-cancel"
                            onClick={() => setOpen(false)}
                        >
                            {t.buttonClose || 'Close'}
                        </button>
                        <div />
                    </div>
                </div>
            </div>
        );
    }

    // react-select options + styles (same as AddDevice)
    const organizationOptions = organizations.map(org => ({
        value: org.id,
        label: org.name
    }));

    const selectedOrganizationOption = organizationOptions.find(
        option => option.value === formData.organization_id
    );
    const selectedReportDeviceOptions = reportDeviceOptions.filter((option) =>
        formData.dashboard_report_devices.includes(Number(option.value))
    );
    const selectedExampleOption = examples.find(
        option => option.value === formData.dashboard_example_id
    );

const customStyles = {
  control: (provided, state) => ({
    ...provided,
    backgroundColor: 'var(--rs-control-bg)',
    borderColor: state.isFocused
      ? 'var(--rs-control-border-focus)'
      : 'var(--rs-control-border)',
    boxShadow: state.isFocused ? 'var(--rs-control-focus-shadow)' : 'none',
    '&:hover': {
      borderColor: 'var(--rs-control-border-hover)',
    },
    color: 'var(--rs-text)',
    fontSize: 'calc(var(--body-font-size) * var(--font-size-multiplier))',
    minHeight: '44px',
    borderRadius: 8,
    cursor: 'pointer',
  }),

  singleValue: (provided) => ({
    ...provided,
    color: 'var(--rs-text)',
    fontSize: 'calc(var(--body-font-size) * var(--font-size-multiplier))',
  }),

  input: (provided) => ({
    ...provided,
    color: 'var(--rs-text)',
    fontSize: 'calc(var(--body-font-size) * var(--font-size-multiplier))',
  }),

  placeholder: (provided) => ({
    ...provided,
    color: 'var(--rs-placeholder)',
    fontSize: 'calc(var(--body-font-size) * var(--font-size-multiplier))',
  }),

  menu: (provided) => ({
    ...provided,
    backgroundColor: 'var(--rs-menu-bg)',
    border: '1px solid var(--rs-menu-border)',
    borderRadius: 8,
    overflow: 'hidden',
    zIndex: 9999,
  }),

  option: (provided, state) => ({
    ...provided,
    backgroundColor: state.isSelected
      ? 'var(--rs-option-selected-bg)'
      : state.isFocused
      ? 'var(--rs-option-hover-bg)'
      : 'var(--rs-option-bg)',
    color: state.isSelected ? 'var(--rs-option-selected-text)' : 'var(--rs-text)',
    fontSize: 'calc(var(--body-font-size) * var(--font-size-multiplier))',
    '&:active': {
      backgroundColor: 'var(--rs-option-active-bg)',
    },
    cursor: 'pointer',
  }),

  dropdownIndicator: (provided) => ({
    ...provided,
    color: 'var(--rs-indicator)',
    paddingRight: 12,
    '&:hover': {
      color: 'var(--rs-indicator-hover)',
    },
  }),

  clearIndicator: (provided) => ({
    ...provided,
    color: 'var(--rs-indicator)',
    '&:hover': {
      color: 'var(--rs-indicator-hover)',
    },
  }),

  indicatorSeparator: () => ({
    display: 'none',
  }),
};

    // MAIN MODAL
    return (
        <div
            id="modal"
            style={{
                direction: isRtl ? 'rtl' : 'ltr'
            }}
        >
            <Alert
                open={alertState.open}
                close={() =>
                    setAlertState({ ...alertState, open: false })
                }
                message={alertState.message}
                type={alertState.type}
                position={alertState.position}
            />

            <div
                id="popup"
                className="dashboard-modal"
                style={{
                    animationName: isRtl ? 'reverse-add-zoom' : 'add-zoom',
                    fontSize:
                        'calc(var(--body-font-size) * var(--font-size-multiplier))'
                }}
            >
                {/* HEADER */}
                <div className="dashboard-header">
                    <div className="dashboard-header-icon">
                        <AddNewIcon />
                    </div>
                    <div className="dashboard-header-text">
                        <h2>{t.title}</h2>
                        <p>{t.description}</p>
                    </div>
                </div>

                <div className="modal-divider" />

                {/* BODY */}
                <div className="modal-content dashboard-content">
                    <div className="form-section template-mode-section">
                        <div className="template-mode-buttons">
                            <button
                                type="button"
                                className={`btn btn-mode-template ${
                                    creationMode === 'blank'
                                        ? 'btn-mode-template-active'
                                        : 'btn-mode-template-inactive'
                                }`}
                                onClick={() => handleModeSwitch('blank')}
                            >
                                {t.modeBlank || 'Blank Dashboard'}
                            </button>
                            <button
                                type="button"
                                className={`btn btn-mode-template ${
                                    creationMode === 'example'
                                        ? 'btn-mode-template-active'
                                        : 'btn-mode-template-inactive'
                                }`}
                                onClick={() => handleModeSwitch('example')}
                            >
                                {t.modeExample || 'From Example'}
                            </button>
                        </div>
                    </div>

                    {/* Dashboard Name */}
                    <div className="form-section">
                        <div className="form-section-title">
                            <span className="form-section-icon">
                                <NameIcon />
                            </span>
                            <span className="form-section-title-text">
                                {t.labelName}
                            </span>
                        </div>

                        <div className="form-item">
                            <div className="field-with-counter">
                                <input
                                    name="name"
                                    type="text"
                                    className="add-column"
                                    placeholder={t.placeholderName}
                                    maxLength={50}
                                    value={formData.name}
                                    onChange={onChange}
                                />
                                <span className="field-counter">
                                    {formData.name.length} / 50
                                </span>
                            </div>
                        </div>
                    </div>

                    {creationMode === 'example' && (
                        <div className="form-section">
                            <div className="form-section-title">
                                <span className="form-section-icon">
                                    <TemplateIcon />
                                </span>
                                <span className="form-section-title-text">
                                    {t.labelExample || 'Dashboard Example'}
                                </span>
                            </div>

                            <div className="form-item">
                                <Select
                                    id="dashboard-example"
                                    options={examples}
                                    value={selectedExampleOption || null}
                                    onChange={handleExampleChange}
                                    placeholder={
                                        loadingExamples
                                            ? t.loadingExamples || 'Loading examples...'
                                            : t.placeholderExample || 'Select an Example'
                                    }
                                    isClearable
                                    isDisabled={loadingExamples}
                                    styles={customStyles}
                                />
                            </div>
                        </div>
                    )}

                    {/* Organization */}
                    <div className="form-section">
                        <div className="form-section-title">
                            <span className="form-section-icon">
                                <OrgNameIcon />
                            </span>
                            <span className="form-section-title-text">
                                {t.labelOrg}
                            </span>
                        </div>

                        <div className="form-item">
                            <Select
                                id="organization"
                                options={organizationOptions}
                                value={selectedOrganizationOption || null}
                                onChange={handleOrgSelect}
                                styles={customStyles}
                                placeholder={
                                    t.placeholderSelectOrg ||
                                    'Select Organization'
                                }
                                isClearable
                            />
                        </div>
                    </div>

                    <div className="form-section">
                        <div className="form-section-title">
                            <span className="form-section-icon"><TemplateIcon /></span>
                            <span className="form-section-title-text">{t.labelDashboardReportDevices || 'Dashboard report devices'}</span>
                        </div>
                        <div className="form-item">
                            <Select
                                id="dashboard-report-devices"
                                isMulti
                                isSearchable
                                isClearable
                                closeMenuOnSelect={false}
                                options={reportDeviceOptions}
                                value={selectedReportDeviceOptions}
                                onChange={(selected) => setFormData((prev) => ({
                                    ...prev,
                                    dashboard_report_devices: (selected || []).map((option) => Number(option.value)),
                                }))}
                                isDisabled={!formData.organization_id || loadingReportDevices}
                                isLoading={loadingReportDevices}
                                styles={customStyles}
                                placeholder={t.placeholderDashboardReportDevices || 'Search and select report devices (empty = all dashboard devices)'}
                            />
                            <div className="edit-helper" style={{ marginTop: 6 }}>
                                {t.dashboardReportDevicesHelper || 'Optional. When Include all is used, only these devices are included. Leave empty to keep the current all-devices behavior.'}
                            </div>
                        </div>
                    </div>

                    {creationMode === 'blank' && (
                        <div className="form-section">
                            <div className="form-section-title">
                                <span className="form-section-icon">
                                    <TemplateIcon />
                                </span>
                                <span className="form-section-title-text">
                                    {webDashboardT.layoutModeLabel || 'Layout Mode'}
                                </span>
                            </div>

                            <div className="form-item">
                                <WebDashboardLayoutModeField
                                    value={formData.initialTabLayoutMode}
                                    onChange={(nextMode) =>
                                        setFormData((prev) => ({
                                            ...prev,
                                            initialTabLayoutMode: nextMode,
                                        }))
                                    }
                                    title={webDashboardT.layoutModeLabel || 'Layout Mode'}
                                    helperText={
                                        webDashboardT.layoutModeHelper ||
                                        'Choose how widget sizes and positions are stored for this tab.'
                                    }
                                    translations={webDashboardT}
                                />
                            </div>
                        </div>
                    )}

                    {showAiReportTypeField && (
                        <AiReportTypeField
                            value={formData.type}
                            onChange={value => setFormData(prev => ({ ...prev, type: value }))}
                            options={aiReportTypeOptions}
                            styles={customStyles}
                            label="AI Report Type"
                            placeholder="Select AI report type"
                        />
                    )}

                    {/* Description */}
                    <div className="form-section">
                        <div className="form-section-title">
                            <span className="form-section-icon">
                                <TemplateIcon />
                            </span>
                            <span className="form-section-title-text">
                                {t.labelDesc}
                            </span>
                        </div>

                        <div className="form-item">
                            <textarea
                                className="add-column"
                                name="description"
                                placeholder={t.placeholderDesc}
                                value={formData.description}
                                onChange={onChange}
                                maxLength={120}
                                style={{
                                    height: '160px',
                                    resize: 'none'
                                }}
                            />
                            <span
                                style={{
                                    alignSelf: 'flex-end',
                                    marginTop: 4,
                                    fontSize: 11,
                                    color: 'rgba(200, 220, 255, 0.75)'
                                }}
                            >
                                {formData.description.length} / 120
                            </span>
                        </div>
                    </div>

                    <div className="form-section">
                        <WorkDaysHoursPicker
                            value={formData.work_days_hours}
                            onChange={(nextRows) =>
                                setFormData((prev) => ({ ...prev, work_days_hours: nextRows }))
                            }
                            labels={{
                                title: t.workDaysHoursTitle || 'Work days and hours',
                                sameShift: t.sameShift || 'Use same shift start / shift end for all work days',
                            }}
                        />
                    </div>
                </div>

                {/* FOOTER */}
                <div className="modal-footer dashboard-footer">
                    <button
                        className="btn btn-cancel"
                        onClick={() => setOpen(false)}
                        disabled={submitting}
                    >
                        {t.buttonCancel}
                    </button>
                    <button
                        className="btn btn-apply dashboard-primary-btn"
                        onClick={createDashboard}
                        disabled={submitting}
                    >
                        {t.buttonDone}
                    </button>
                </div>
            </div>
        </div>
    );
};
